import { mapActions } from 'vuex';

import { api } from '_/services/api';

// Polling interval. 30s strikes the balance: dashboard feels live, the
// FastAPI endpoint stays well under Anthropic / Voyage rate budgets, and
// the Postgres queries are cheap enough to run every 30s on a hot loop.
const POLL_INTERVAL_MS = 30000;


function statusBadge (status) {
    switch (status) {
        case 'draft_ready':     return 'label-success';
        case 'duplicate_cluster': return 'label-info';
        case 'failed':          return 'label-danger';
        case 'filtered_out':    return 'label-warning';
        case 'tagging':
        case 'tagged':
        case 'extracting':
        case 'rewriting':
        case 'fetched':         return 'label-primary';
        case 'classifier_retry': return 'label-warning';
        case 'duplicate':       return 'label-default';
        default:                return 'label-default';
    }
}


function formatRelative (iso) {
    if (!iso) return '—';
    const t = new Date(iso).getTime();
    const dt = Date.now() - t;
    if (Number.isNaN(t)) return '—';
    if (dt < 60_000)       return Math.floor(dt / 1000) + 's ago';
    if (dt < 3_600_000)    return Math.floor(dt / 60_000) + 'm ago';
    if (dt < 86_400_000)   return Math.floor(dt / 3_600_000) + 'h ago';
    return Math.floor(dt / 86_400_000) + 'd ago';
}


export default {
    name: 'PipelineMonitor',

    data () {
        return {
            summary: null,
            snapshotAt: null,
            loading: false,
            error: null,
            _pollHandle: null,
        };
    },

    computed: {
        breadcrumbItems () {
            return [{
                text: 'Pipeline Monitor',
                link: 'pipeline-monitor',
            }];
        },

        totalCost24h () {
            if (!this.summary || !this.summary.cost) return 0;
            return this.summary.cost.by_purpose_24h.reduce(
                (acc, c) => acc + (c.cost_usd || 0), 0,
            );
        },
        gatePublishPct () {
            if (!this.summary) return '—';
            const v = this.summary.editorial_gate.verdicts_24h;
            const total = (v.publish || 0) + (v.reject || 0) + (v.borderline || 0);
            if (!total) return '—';
            return Math.round(100 * (v.publish || 0) / total);
        },
    },

    methods: {
        ...mapActions('ui', [
            'animateWrapper',
            'setBodyClass',
            'setBreadcrumbItems',
        ]),

        statusBadge,
        formatRelative,

        async refresh () {
            this.loading = true;
            try {
                const response = await api.get('admin/pipeline/summary');
                // PCMS API client wraps responses as {message, body} on most
                // endpoints, but the pipeline proxy forwards the raw FastAPI
                // payload — handle both shapes defensively.
                const payload = response && response.body
                    ? response.body
                    : response;
                this.summary = payload;
                this.snapshotAt = payload.snapshot_at;
                this.error = null;
            } catch (exc) {
                this.error = (exc && exc.message)
                    || (exc && exc.response && exc.response.data && exc.response.data.message)
                    || 'Could not load pipeline state.';
            } finally {
                this.loading = false;
            }
        },

        startPolling () {
            this.refresh();
            this._pollHandle = setInterval(() => this.refresh(), POLL_INTERVAL_MS);
        },

        stopPolling () {
            if (this._pollHandle) {
                clearInterval(this._pollHandle);
                this._pollHandle = null;
            }
        },
    },

    mounted () {
        this.$nextTick(function () {
            this.setBodyClass('admin');
            this.setBreadcrumbItems(this.breadcrumbItems);
            this.animateWrapper();
            this.startPolling();
        });
    },

    beforeDestroy () {
        this.stopPolling();
    },
};
